import React, { useState } from 'react';
import { Loader2, ArrowLeft, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'components/ui/use-toast';
import type { Subject } from 'modules/subject/types';
import { useCreateBankQuestion } from 'modules/questionBank';
import type { CreateBankQuestionData } from 'modules/questionBank/api';
import type { BankQuestion } from 'modules/questionBank/types';
import {
  useGenerationFields,
  GENERATION_LANGUAGES,
  defaultGenerationLanguage,
} from 'modules/generationFields';
import { BankQuestionForm } from './BankQuestionForm';
import { FieldPromptInput } from './FieldPromptInput';
import { SubjectSelect } from './SubjectSelect';
import { isAdvancedQuestionType } from 'pages/QuestionBank/questionTypeMeta';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import {
  GenerateQuestionsByTopic,
  type AIGeneratedQuestion,
} from 'modules/test/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subjects: Subject[];
  defaultSubjectId?: string;
}

// Types the backend AI generator actually supports (mirrors GenerateQuestionsByTopicDto).
// `labelKey` is an i18n key resolved at render time (see locale files).
const AI_TYPES: { value: string; labelKey: string }[] = [
  { value: 'MULTIPLE_CHOICE', labelKey: 'single_choice' },
  { value: 'MULTIPLE_RESPONSE', labelKey: 'multiple_choice' },
  { value: 'TRUE_FALSE', labelKey: 'true_false' },
  { value: 'SHORT_ANSWER', labelKey: 'short_answer' },
  { value: 'FILL_IN_BLANKS', labelKey: 'fill_in_blanks' },
  { value: 'MATCHING', labelKey: 'matching' },
  { value: 'REORDER', labelKey: 'reorder' },
  { value: 'OPEN_ENDED', labelKey: 'open_ended' },
  { value: 'PASSAGE', labelKey: 'passage' },
];

// Map a freshly generated question onto the shape BankQuestionForm seeds from.
// Only the fields the form reads matter; id/org/timestamps are placeholders since
// nothing is persisted until the user submits the form.
function toInitialData(q: AIGeneratedQuestion, subjectId: string): BankQuestion {
  return {
    id: '',
    organizationId: '',
    subjectId,
    type: q.type as BankQuestion['type'],
    prompt: q.prompt,
    options: q.options,
    matchPairs: q.matchPairs,
    correctAnswer: q.correctAnswer ?? '',
    points: 1,
    explanation: q.explanation,
    difficulty: q.difficulty,
    answerMode: q.answerMode,
    acceptedAnswers: q.acceptedAnswers,
    maxWords: q.maxWords,
    matchingOptions: q.matchingOptions,
    blanks: q.blanks,
    reorderItems: q.reorderItems,
    correctOrder: q.correctOrder,
    passageText: q.passageText,
    passageTitle: q.passageTitle,
    subQuestions: q.subQuestions as BankQuestion['subQuestions'],
    createdAt: '',
    updatedAt: '',
  } as BankQuestion;
}

export const GenerateWithAIModal: React.FC<Props> = ({
  isOpen,
  onClose,
  subjects,
  defaultSubjectId,
}) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [subjectId, setSubjectId] = useState(defaultSubjectId ?? '');
  const [topic, setTopic] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldPrompt, setFieldPrompt] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [language, setLanguage] = useState(() => defaultGenerationLanguage(i18n.language));
  const [type, setType] = useState<string>('MULTIPLE_CHOICE');
  const [generated, setGenerated] = useState<AIGeneratedQuestion | null>(null);
  // Advanced (non-basic) question types are a Growth+ capability.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();
  const isLockedType = (value: string) => !canAdvancedTypes && isAdvancedQuestionType(value);

  const { fields } = useGenerationFields();
  const createQuestion = useCreateBankQuestion();

  const generate = useMutation({
    mutationFn: async () => {
      const res = await GenerateQuestionsByTopic({
        topic: topic.trim(),
        questionCount: 1,
        questionTypes: [type],
        difficulty,
        language,
        subjectId: subjectId || undefined,
        fieldKey: fieldKey || undefined,
        fieldPrompt: fieldPrompt.trim() || undefined,
      });
      return res.data?.data?.questions ?? [];
    },
    onSuccess: (questions) => {
      const first = questions[0] ?? null;
      if (!first) {
        toast.error(t('no_question_generated'), { description: t('model_returned_nothing') });
        return;
      }
      setGenerated(first);
    },
    onError: (error: any) => {
      toast.error(t('generation_failed'), {
        description: error?.response?.data?.message || t('could_not_generate_question'),
      });
    },
  });

  const backToForm = () => setGenerated(null);

  const handleClose = () => {
    setGenerated(null);
    onClose();
  };

  const canGenerate = Boolean(subjectId) && topic.trim().length > 0 && Boolean(type);

  const handleSubmit = async (data: CreateBankQuestionData) => {
    await createQuestion.mutateAsync(data);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('generate_question_with_ai')} size="lg">
      {!generated ? (
        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-1 block">{t('subject')} *</Label>
            <SubjectSelect
              value={subjectId}
              onChange={setSubjectId}
              subjects={subjects}
            />
          </div>

          <FieldPromptInput
            fields={fields}
            fieldKey={fieldKey}
            fieldPrompt={fieldPrompt}
            onFieldKeyChange={setFieldKey}
            onFieldPromptChange={setFieldPrompt}
          />

          <div>
            <Label className="mb-1 block">{t('topic')} *</Label>
            <Input
              placeholder={t('topic_placeholder')}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">{t('difficulty')}</Label>
              <Select value={difficulty} onValueChange={(v) => setDifficulty(v as 'easy' | 'medium' | 'hard')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">{t('easy')}</SelectItem>
                  <SelectItem value="medium">{t('medium')}</SelectItem>
                  <SelectItem value="hard">{t('hard')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-1 block">{t('language')}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GENERATION_LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">{t('question_type')}</Label>
            <RadioGroup
              value={type}
              onValueChange={(value) => {
                // Picking a locked advanced type opens the upgrade dialog and
                // keeps the current (basic) selection instead of switching.
                if (isLockedType(value)) {
                  openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
                  return;
                }
                setType(value);
              }}
              className="grid grid-cols-2 gap-2"
            >
              {AI_TYPES.map((tp) => (
                <label key={tp.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value={tp.value} />
                  {t(tp.labelKey)}
                  {isLockedType(tp.value) && (
                    <Crown className="size-3 text-amber-500" aria-hidden />
                  )}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button onClick={() => generate.mutate()} disabled={!canGenerate || generate.isPending}>
              {generate.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('generate')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('review_generated_question')}
            </p>
            <Button variant="ghost" size="sm" onClick={backToForm} disabled={createQuestion.isPending}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('back')}
            </Button>
          </div>

          <BankQuestionForm
            subjects={subjects}
            initialData={toInitialData(generated, subjectId)}
            defaultSubjectId={subjectId}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            isSubmitting={createQuestion.isPending}
          />
        </div>
      )}
    </Modal>
  );
};

export default GenerateWithAIModal;
